'use client'
import BuyersAlertBlocker from '@/app/[lng]/components/BlockerModal/buyersAlertBlocker'
import {
  useContactBuyerBuyerAlertGTMEvent,
  useGTMBuyerTracker,
} from '@/app/[lng]/components/GTMTrackers/GTMBuyerTracker'
import { gtmFromRedirectedToCompany } from '@/app/[lng]/components/GTMTrackers/GTMCompanyTracker'
import PlanLogo from '@/app/[lng]/components/PlanLogo'
import DetailsPageSkeleton from '@/app/[lng]/components/Skeletons/DetailsPageSkeleton'
import ShortCardSkeleton from '@/app/[lng]/components/Skeletons/shortCardSkeleton'
import { useGetSingleAlertQuery } from '@/app/globalRedux/api/alertSlice'
import { RootState } from '@/app/globalRedux/store'
import { useTranslation } from '@/app/i18n/client'
import { Contact } from '@/app/types/alertType'
import {
  BuyerAlert,
  SingleBuyerAlertResponse,
} from '@/app/types/singleBuyerAlertType'
import { AvailableMonthList } from '@/components/AvailableMonthList'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import ShareSocials from '@/components/ui/ShareSocials'
import { Skeleton } from '@/components/ui/skeleton'
import { companyProfileRedirection } from '@/lib/redirectionUtils'
import Image from 'next/image'
import Link from 'next/link'
import { useParams, usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import RelatedAlerts from './RelatedAlerts'
import ImagePreview from '@/components/ui/imagePreview'
import { Button } from '@/components/ui/button'
import { useRouter } from 'next/navigation'

interface BuyersAlertDetailProps {
  Alert_id: string | number
}

const BuyersAlertDetail = ({ Alert_id }: BuyersAlertDetailProps) => {
  const params = useParams()
  const router = useRouter()
  const pathName = usePathname()
  const { data, isLoading } = useGetSingleAlertQuery<{
    data: SingleBuyerAlertResponse
    isLoading: boolean
  }>(Alert_id)

  const { userSubscription, loggedIn } = useSelector(
    (state: RootState) => state.user
  )
  const alertData = data?.buyer_alert
  useGTMBuyerTracker(
    Number((data?.buyer_alert as BuyerAlert)?.alert?.company?.id)
  )
  const sendContactBuyerEvent = useContactBuyerBuyerAlertGTMEvent()

  const { t } = useTranslation()
  const [open, setOpen] = useState<boolean>(true)
  useEffect(() => {
    if (
      typeof data?.buyer_alert == 'string' &&
      data?.buyer_alert === 'Access Exhausted'
    ) {
      setOpen(true)
    }
  }, [data])

  return (
    <>
      {typeof data?.buyer_alert === 'string' ? (
        <>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
              hideCloseIcon={true}
              className="border-0 p-0 max-w-[626px] min-h-[216px] bg-[white] outline-none py-7"
              onInteractOutside={(e) => {
                e.preventDefault()
              }}
            >
              <BuyersAlertBlocker name={'Logged out'} limit={'-1'} />
            </DialogContent>
          </Dialog>
          <div className="basis-4/5">
            <Breadcrumb className="pb-7 mobile:pb-3">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/buyers-listing">
                    {t('Home')}
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t('Buyers Alert Details')}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>

            <Skeleton className="h-[425px] w-[450px] rounded-xl" />

            <Separator className="mt-5 w-[954px] mobile-width" />

            <div className="flex gap-4">
              <Skeleton className="w-20 h-40" />
              <Skeleton className="w-20 h-40" />
              <Skeleton className="w-20 h-40" />
            </div>
          </div>
        </>
      ) : (
        <div className="basis-4/5">
          <Breadcrumb className="pb-7 mobile:pb-3">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="/buyers-listing">
                  {t('Home')}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{t('Buyers Alert Details')}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          {!isLoading ? (
            <div className="flex flex-row w-full gap-5 mobile:relative">
              <ImagePreview
                images={data?.buyer_alert?.alert?.images ?? []}
                title={`${data?.buyer_alert?.alert?.title ?? ''}`}
                cardType={'alert'}
              />
              <div className="flex flex-col ">
                <div className="flex justify-between items-start relative pr-1">
                  <div className="w-[90%]">
                    <p className="text-xs text-light_black">
                      <span className="mr-[5px]">{t('Published Date')}:</span>
                      {data?.buyer_alert?.alert?.publication_date
                        ? data.buyer_alert.alert.publication_date
                        : t('Data not present')}
                    </p>
                    <h2 className="font-ns_bold text-lg flex gap-3">
                      {data?.buyer_alert?.alert?.title ?? ''}
                    </h2>
                  </div>
                  <div className="mobile:absolute mobile:right-0">
                    <ShareSocials
                      sharAbleLink={`${window.location.origin}/${params?.lng}/buyers_alert/${Alert_id}`}
                      shareText={data?.buyer_alert?.alert?.title ?? ''}
                      shareBg={true}
                    />
                  </div>
                </div>
                <div className="py-2 mb-3">
                  {data?.buyer_alert?.alert?.product_type?.length > 0 && (
                    <span className="text-sm border bg-chip_bg border-chip_border rounded-full  px-2">
                      {t(
                        data?.buyer_alert?.alert?.product_type
                          ?.map((item) => item?.name)
                          ?.join(' & ')
                      )}
                    </span>
                  )}
                </div>
                <div className="mobile:flex flex-col hidden">
                  <div className="h-[130px] w-full relative">
                    <div className="h-[130px] w-full bg-[#ededed] overflow-hidden rounded-sm rounded-tr-sm rounded-br-none">
                      <Image
                        loading="lazy"
                        width={409}
                        height={306}
                        src={
                          data?.buyer_alert?.alert?.images?.length > 0
                            ? data?.buyer_alert?.alert?.images?.[0]?.url
                            : '/assets/Icons/noImage/noImage2.svg'
                        }
                        alt="offer-img"
                        className={`h-[306px] w-[409px] mobile:w-full mobile:h-[130px] ${
                          data?.buyer_alert?.alert?.images?.length > 0
                            ? 'w-full h-full object-cover'
                            : 'w-full h-full object-contain'
                        } `}
                      />
                    </div>
                    <span className="text-sm bg-orange_tag w-fit rounded-l-sm text-white absolute p-1 px-3 right-0 bottom-0">
                      {t('ALERT')}
                    </span>
                  </div>
                  {data?.buyer_alert?.alert?.images?.length > 1 && (
                    <div className="flex h-20 gap-2 mt-3 flex-warp">
                      {data?.buyer_alert?.alert?.images
                        ?.slice(1, data?.buyer_alert?.alert?.images?.length)
                        ?.map((item: any, index: number) => (
                          <Image
                            width={80}
                            key={index}
                            height={75}
                            src={
                              item?.url ?? '/assets/Icons/noImage/noImage2.svg'
                            }
                            alt=""
                          />
                        ))}
                    </div>
                  )}
                </div>
                <div className="flex flex-col gap-3 font-ns_bold text-sm mobile:gap-2 mobile:mt-2">
                  <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                    <p className="text-base text-[16px] font-ns_bold mobile:flex">
                      {t('Origin')}
                      <span className="hidden mobile:block mobile:mx-1">:</span>
                    </p>
                    <span className="font-light">
                      {data?.buyer_alert?.alert?.origins?.length == 0 &&
                        t('NA')}
                      {data?.buyer_alert?.alert?.origins?.length > 0 && (
                        <span className=" flex flex-row">
                          {data?.buyer_alert?.alert?.origins
                            ?.map((item: any) => item.name)
                            ?.join(', ')}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                    <p className="text-base  text-[16px] font-ns_bold mobile:flex">
                      {t('Variety')}
                      <span className="mobile:block hidden mobile:mx-1">:</span>
                    </p>
                    <p className="font-light gap-1">
                      {data?.buyer_alert?.alert?.varieties?.length > 0 ? (
                        <div className="flex flex-wrap w-full gap-[2px]">
                          {data?.buyer_alert?.alert?.varieties?.map(
                            (variety, index) => (
                              <span
                                key={variety.id}
                                className="whitespace-nowrap"
                              >
                                {variety.name}
                                {index <
                                  (data?.buyer_alert as BuyerAlert)?.alert
                                    ?.varieties.length -
                                    1 && ', '}
                              </span>
                            )
                          )}
                        </div>
                      ) : (
                        t('NA')
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                    <p className="text-base  text-[16px] font-ns_bold mobile:flex">
                      {t('Quality')}
                      <span className="mobile:block hidden mobile:mx-1">:</span>
                    </p>
                    <p className="font-light gap-[1px ]">
                      {typeof alertData !== 'string' &&
                      alertData?.alert?.quality?.length === 0
                        ? t('NA')
                        : typeof alertData !== 'string' &&
                          alertData?.alert?.quality
                            ?.map((quality) => quality.name)
                            ?.join(', ')}
                    </p>
                  </div>
                  <div className="flex flex-col mobile:flex-col-reverse gap-2">
                    <div>
                      <p className="text-base  text-[16px] font-ns_bold">
                        {t('When')}
                      </p>
                      <span className="flex flex-row text-primary-foreground w-full gap-1.5">
                        <AvailableMonthList
                          availableMonths={
                            data?.buyer_alert?.alert?.available_months?.months
                          }
                        />
                      </span>
                    </div>
                    <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                      <p className="text-base  text-[16px] font-ns_bold mobile:flex">
                        {t('Payment Type')}
                        <span className="mobile:block hidden mobile:mx-1">
                          :
                        </span>
                      </p>
                      <p className="font-light">
                        {data?.buyer_alert?.alert?.payment_type
                          ? `${data.buyer_alert?.alert?.payment_type?.name}`
                          : t('NA')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                      <p className="text-base  text-[16px] font-ns_bold mobile:flex">
                        {t('Payment Method')}
                        <span className="mobile:block hidden mobile:mx-1">
                          :
                        </span>
                      </p>
                      <p className="font-light">
                        {data?.buyer_alert?.alert?.payment_method
                          ? `${data.buyer_alert?.alert?.payment_method}`
                          : t('NA')}
                      </p>
                    </div>
                    <div className="flex flex-col gap-1 mobile:flex-row mobile:items-baseline">
                      <p className="text-base  text-[16px] font-ns_bold mobile:flex">
                        {t('Contact Preference')}
                        <span className="mobile:block hidden mobile:mx-1">
                          :
                        </span>
                      </p>
                      <p className="font-light !capitalize">
                        {(data?.buyer_alert?.contact as Contact)
                          ?.contact_preference
                          ? (data?.buyer_alert?.contact as Contact)
                              ?.contact_preference === 'both'
                            ? t('Email or Phone')
                            : t(
                                (data?.buyer_alert?.contact as Contact)
                                  ?.contact_preference
                              )
                          : t('NA')}
                      </p>
                    </div>
                  </div>
                  <div className="shadow-[0_0_4px_0_rgba(0,0,0,0.16)] p-4 py-3 rounded flex gap-4 mt-2">
                    <Image
                      loading="lazy"
                      width={45}
                      height={45}
                      src="/assets/Icons/boxIcon.svg"
                      alt=""
                    />
                    <div className="flex flex-col gap-1">
                      <p>
                        <span className="font-ns_bold">{t('Caliber')} : </span>
                        <span className="font-light">
                          {data?.buyer_alert?.alert?.size
                            ? data?.buyer_alert?.alert?.size
                            : t('NA')}
                        </span>
                      </p>
                      <p>
                        <span className="font-ns_bold">
                          {t('Packaging')} :{' '}
                        </span>
                        <span className="font-light">
                          {data?.buyer_alert?.alert?.packaging
                            ? data?.buyer_alert?.alert?.packaging
                            : t('NA')}
                        </span>
                      </p>
                    </div>
                  </div>
                  <div className="shadow-[0_0_4px_0_rgba(0,0,0,0.16)] p-4 py-2 flex gap-4">
                    <Image
                      loading="lazy"
                      width={45}
                      height={45}
                      src="/assets/Icons/truck.svg"
                      alt=""
                    />
                    <div className="flex flex-col gap-1">
                      <p className="flex gap-1">
                        <span className="font-ns_bold">{t('Quantity')} : </span>
                        <span className="font-light gap-[2px] flex">
                          {data?.buyer_alert?.alert?.quantity_from
                            ? data?.buyer_alert?.alert?.quantity_from
                            : t('NA')}
                          {data?.buyer_alert?.alert?.quantity_to
                            ? ` ${t('to')} ${data?.buyer_alert?.alert?.quantity_to}`
                            : ''}
                        </span>
                        {data?.buyer_alert?.alert?.quantity_unit?.name ? (
                          <span className="font-light">
                            {data?.buyer_alert?.alert?.quantity_unit?.name}
                          </span>
                        ) : (
                          <span className="font-light">{''}</span>
                        )}

                        <span className="font-light">
                          {data?.buyer_alert?.alert?.time_frequency?.name || ''}
                        </span>
                      </p>
                      <p>
                        <span className="font-ns_bold">
                          {t('Delivery Point')} :{' '}
                        </span>
                        <span className="font-light">
                          {data?.buyer_alert?.alert?.delivery_city?.name
                            ? `${data?.buyer_alert?.alert?.delivery_city?.name}, `
                            : ''}
                          {data?.buyer_alert?.alert?.delivery_country?.name
                            ? data?.buyer_alert?.alert?.delivery_country?.name
                            : t('NA')}
                        </span>
                      </p>
                    </div>
                  </div>
                  <span className="font-ns_bold mt-2 flex gap-3">
                    {!['Free', 'Silver'].includes(userSubscription) &&
                      loggedIn && (
                        <>
                          <Link
                            className="font-bold"
                            href={companyProfileRedirection(
                              data?.buyer_alert?.alert?.company?.industry
                                ?.name ?? '',
                              data?.buyer_alert?.alert?.company?.friendly_url ??
                                '',
                              String(params?.lng) ?? 'en'
                            )}
                            onClick={() => {
                              gtmFromRedirectedToCompany(
                                'buyer_alert_detail',
                                (data?.buyer_alert as BuyerAlert)?.alert
                                  ?.company?.friendly_url ?? '',
                                (
                                  data?.buyer_alert as BuyerAlert
                                )?.alert?.company?.id?.toString() ?? ''
                              )
                            }}
                          >
                            {data?.buyer_alert?.alert?.company?.name}
                          </Link>
                          {data?.buyer_alert?.alert?.company?.subscription &&
                            !['Free', 'Silver'].includes(
                              data?.buyer_alert?.alert?.company?.subscription
                            ) && (
                              <PlanLogo
                                planType={
                                  data?.buyer_alert?.alert?.company
                                    ?.subscription
                                }
                              />
                            )}
                        </>
                      )}
                  </span>
                  <div className="py-4 pt-2  mobile:py-2 mobile:w-full">
                    <Button
                      variant="secondary"
                      size="sx"
                      className="mobile:w-full"
                      id="contact_buyer_click"
                      onClick={() => {
                        sendContactBuyerEvent({
                          buyerAlertID: Number(data?.buyer_alert?.alert?.id),
                          companyID: Number(
                            data?.buyer_alert?.alert?.company?.id
                          ),
                        })
                        router.push(`${pathName}/contact`)
                      }}
                    >
                      {t('Contact Buyer')}
                    </Button>
                  </div>
                  <div className="flex flex-col gap-y-1 text-[16px] font-bold mobile:flex-row">
                    <p className="mb-[10px] mobile:mb-0 mobile:mr-1 mobile:flex">
                      {t('Additional Information')}
                      <span className="mobile:block hidden mobile:mx-1">:</span>
                    </p>
                    {data?.buyer_alert?.alert?.comment ? (
                      <span className="font-light">
                        {data?.buyer_alert?.alert?.comment}
                      </span>
                    ) : (
                      <span className="font-light">{t('NA')}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <DetailsPageSkeleton />
          )}
          <Separator className="mt-5 w-[954px] mobile-width" />
          {!isLoading ? (
            <RelatedAlerts Alert_id={data?.buyer_alert?.alert?.uuid} />
          ) : (
            <div className="flex gap-4 pt-4">
              <ShortCardSkeleton count={3} />
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default BuyersAlertDetail
